import { CognitoUser } from '@aws-amplify/auth';
import { apiRootEndpoint } from '../config';
import { useFlexAssessmentContext } from '../context/context';
import { ApiFailedResponse } from '../types/api-failed-response';
import {
  CreateAccountApiSuccessResponse,
  CreateAccountPayload,
  EnrollInProgramApiResponse,
  EnrollUserInProgramPayload,
  InitializeUserPayload,
  OnboardUserApiResponse,
  UserId,
} from '../types/user-management-api-response';
import {
  BRAZE_EVENTS,
  changeBrazeUser,
  getBrazeDeviceId,
  logBrazeEvent,
} from '../utils/braze-helper';
import {
  AccountCreationFailed,
  UserEnrollmentError,
  UserOnboardingError,
} from '../utils/errors';
import { fetcher } from '../utils/fetcher';
import { trackRegistrationMilestone } from '../utils/google-analytics-helper';
import {
  getMixpanelDistinctId,
  registerUserInMixpanel,
} from '../utils/mixpanel-helper';
import { toCognitoPhone } from '../utils/telephone';

export default function useUserManagement(): Record<string, Function> {
  const { state } = useFlexAssessmentContext();

  const createAccount = async (
    createAccountPayload: CreateAccountPayload
  ): Promise<UserId | Error> => {
    const { utm } = state;
    const brazeDeviceId = await getBrazeDeviceId();

    const requestBody = {
      username: createAccountPayload.email,
      password: createAccountPayload.password,
      givenName: createAccountPayload.firstName,
      optionalUserInfo: {
        givenName: createAccountPayload.firstName,
        familyName: createAccountPayload.lastName,
        ...(createAccountPayload.gender && {
          gender: createAccountPayload.gender,
        }),
        ...(createAccountPayload.dateOfBirth && {
          birthDate: createAccountPayload.dateOfBirth,
        }),
        phoneNumber: toCognitoPhone(createAccountPayload.telephone as string),
        smsPreferences: {
          marketing: createAccountPayload.allowSms,
          personalized: createAccountPayload.allowSms,
        },
      },
      mpDistinctId: getMixpanelDistinctId(),
      brazeDeviceId,
      ...{ utm },
    };

    try {
      const init = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      };
      const res: CreateAccountApiSuccessResponse | ApiFailedResponse =
        await fetcher<CreateAccountApiSuccessResponse | ApiFailedResponse>(
          `${apiRootEndpoint}/v3/registration/register`,
          init
        );

      const userId = res.data?.userId;

      if (!userId) {
        throw new AccountCreationFailed({ res, init });
      }

      trackUser(userId, requestBody);

      return userId;
    } catch (err) {
      throw Error();
    }
  };

  const trackUser = (
    userId: string,
    requestBody: Record<string, any>
  ): void => {
    registerUserInMixpanel(userId, requestBody);
    logBrazeEvent(BRAZE_EVENTS.REGISTERED);
    changeBrazeUser(userId);
    trackRegistrationMilestone(
      requestBody.accessCode,
      requestBody.utm?.utm_source,
      requestBody.utm?.utm_campaign
    );
  };

  const enrollUserInProgram = async (
    userId: string,
    user: CognitoUser,
    requestBody: EnrollUserInProgramPayload
  ): Promise<any> => {
    try {
      const jwtToken = user
        ?.getSignInUserSession()
        ?.getAccessToken()
        .getJwtToken();

      return await fetcher<EnrollInProgramApiResponse | ApiFailedResponse>(
        `${apiRootEndpoint}/v1/users/${userId}/programs`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwtToken}`,
          },
          body: JSON.stringify(requestBody),
        }
      );
    } catch (err) {
      throw new UserEnrollmentError(userId);
    }
  };

  const initializeUser = async (
    userId: string,
    user: CognitoUser,
    requestBody: InitializeUserPayload
  ): Promise<any> => {
    try {
      const jwtToken = user
        ?.getSignInUserSession()
        ?.getAccessToken()
        .getJwtToken();
      return await fetcher<OnboardUserApiResponse | ApiFailedResponse>(
        `${apiRootEndpoint}/v3/users/${userId}/onboarding/initialize`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwtToken}`,
          },
          body: JSON.stringify(requestBody),
        }
      );
    } catch (err) {
      throw new UserOnboardingError(userId);
    }
  };

  return {
    createAccount,
    enrollUserInProgram,
    initializeUser,
  };
}
